import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import PiInput, { formatAffineLatexPreview, parsePiLikeInput } from "../../../components/inputs/PiInput";
import SelectInput from "../../../components/inputs/SelectInput";
import { TrigEquationGraph } from "./TrigEquationIntroTask";

export const type = "AdvancedTrigEquationTask";

const METHODS = [
  { value: "reduction", label: "Wzór redukcyjny: zamiana sinusa na cosinus lub odwrotnie" },
  { value: "sumToProduct", label: "Zamiana sumy funkcji na iloczyn" },
  { value: "angleAddition", label: "Wzór na cosinus sumy lub różnicy kątów" },
  { value: "powers", label: "Tożsamości i redukcja potęg" },
  { value: "halfDouble", label: "Wzór połówkowego lub podwójnego kąta" },
  { value: "product", label: "Przekształcenie iloczynu i kontrola dziedziny" },
];

const gcd = (a, b) => { let x = Math.abs(a); let y = Math.abs(b); while (y) [x, y] = [y, x % y]; return x || 1; };
const R = (n, d = 1) => { const sign = d < 0 ? -1 : 1; const divisor = gcd(n, d); return { n: (n * sign) / divisor, d: Math.abs(d) / divisor }; };
const F = (an, ad, pn, pd) => ({ anchor: R(an, ad), period: R(Math.abs(pn), Math.abs(pd)) });

const CONFIGS = {
  reduction: [
    {
      validation: { kind: "reductionSpecial" },
      equation: "\\cos\\left(x-\\dfrac{\\pi}{6}\\right)=\\sin x",
      idea: "Zamieniamy sinus na cosinus dopełniającego kąta.",
      steps: [
        "\\sin x=\\cos\\left(\\dfrac{\\pi}{2}-x\\right)",
        "\\cos\\left(x-\\dfrac{\\pi}{6}\\right)=\\cos\\left(\\dfrac{\\pi}{2}-x\\right)",
        "x-\\dfrac{\\pi}{6}=\\dfrac{\\pi}{2}-x+2k\\pi",
        "x=\\dfrac{\\pi}{3}+k\\pi",
      ],
      families: [F(1, 3, 1, 1)],
    },
  ],
  sumToProduct: [
    {
      validation: { kind: "sum", fn: "sin", middle: 2, difference: 1, targetKey: "one" },
      equation: "\\sin x+\\sin 3x=\\sin 2x",
      idea: "Stosujemy wzór na sumę sinusów i wyłączamy wspólny czynnik.",
      steps: [
        "\\sin x+\\sin 3x=2\\sin 2x\\cos x",
        "2\\sin 2x\\cos x=\\sin 2x",
        "\\sin 2x(2\\cos x-1)=0",
        "\\sin 2x=0\\quad\\lor\\quad\\cos x=\\dfrac12",
        "x=k\\dfrac{\\pi}{2}\\quad\\lor\\quad x=\\dfrac{\\pi}{3}+2k\\pi\\quad\\lor\\quad x=-\\dfrac{\\pi}{3}+2k\\pi",
      ],
      families: [F(0, 1, 1, 2), F(1, 3, 2, 1), F(-1, 3, 2, 1)],
    },
  ],
  angleAddition: [
    {
      validation: { kind: "angleAddition", alpha: R(1, 3), direction: 1, beta: R(0, 1) },
      equation: "\\dfrac12\\cos x-\\dfrac{\\sqrt3}{2}\\sin x=1",
      idea: "Rozpoznajemy wzór na cosinus sumy.",
      steps: [
        "\\cos\\left(x+\\dfrac{\\pi}{3}\\right)=1",
        "x+\\dfrac{\\pi}{3}=2k\\pi",
        "x=-\\dfrac{\\pi}{3}+2k\\pi",
      ],
      families: [F(-1, 3, 2, 1)],
    },
    {
      validation: { kind: "angleAddition", alpha: R(1, 8), direction: 1, beta: R(1, 6) },
      equation: "\\cos\\dfrac{\\pi}{8}\\cos x-\\sin\\dfrac{\\pi}{8}\\sin x=\\dfrac{\\sqrt3}{2}",
      idea: "Łączymy dwa składniki we wzór na cosinus sumy.",
      steps: [
        "\\cos\\left(x+\\dfrac{\\pi}{8}\\right)=\\dfrac{\\sqrt3}{2}",
        "x+\\dfrac{\\pi}{8}=\\pm\\dfrac{\\pi}{6}+2k\\pi",
        "x=\\dfrac{\\pi}{24}+2k\\pi\\quad\\lor\\quad x=-\\dfrac{7\\pi}{24}+2k\\pi",
      ],
      families: [F(1, 24, 2, 1), F(-7, 24, 2, 1)],
    },
  ],
  powers: [
    {
      equation: "\\sin^4x+\\cos^4x=\\dfrac12",
      idea: "Redukujemy czwarte potęgi za pomocą jedynki trygonometrycznej i wzoru na podwójny kąt.",
      steps: [
        "\\sin^4x+\\cos^4x=(\\sin^2x+\\cos^2x)^2-2\\sin^2x\\cos^2x",
        "1-\\dfrac12\\sin^2 2x=\\dfrac12",
        "\\sin^2 2x=1",
        "x=\\dfrac{\\pi}{4}+k\\dfrac{\\pi}{2}",
      ],
      families: [F(1, 4, 1, 2)],
    },
    {
      equation: "\\sin^4x=1+\\cos^4x",
      idea: "Korzystamy z różnicy kwadratów i jedynki trygonometrycznej.",
      steps: [
        "\\sin^4x-\\cos^4x=1",
        "(\\sin^2x-\\cos^2x)(\\sin^2x+\\cos^2x)=1",
        "-\\cos 2x=1",
        "x=\\dfrac{\\pi}{2}+k\\pi",
      ],
      families: [F(1, 2, 1, 1)],
    },
  ],
  halfDouble: [
    {
      equation: "2\\cos^2\\dfrac{x}{2}+1=2\\sqrt2\\cos\\dfrac{x}{2}",
      idea: "Traktujemy cosinus połówkowego kąta jak niewiadomą pomocniczą.",
      steps: [
        "t=\\cos\\dfrac{x}{2}",
        "2t^2-2\\sqrt2t+1=0",
        "t=\\dfrac{\\sqrt2}{2}",
        "x=\\dfrac{\\pi}{2}+4k\\pi\\quad\\lor\\quad x=-\\dfrac{\\pi}{2}+4k\\pi",
      ],
      families: [F(1, 2, 4, 1), F(-1, 2, 4, 1)],
    },
    {
      equation: "2\\cos x+3=4\\cos\\dfrac{x}{2}",
      idea: "Zapisujemy cosinus kąta przez cosinus połówkowego kąta.",
      steps: [
        "\\cos x=2\\cos^2\\dfrac{x}{2}-1",
        "4\\cos^2\\dfrac{x}{2}-4\\cos\\dfrac{x}{2}+1=0",
        "\\cos\\dfrac{x}{2}=\\dfrac12",
        "x=\\dfrac{2\\pi}{3}+4k\\pi\\quad\\lor\\quad x=-\\dfrac{2\\pi}{3}+4k\\pi",
      ],
      families: [F(2, 3, 4, 1), F(-2, 3, 4, 1)],
    },
  ],
  product: [
    {
      validation: { kind: "tangentHalf", multiplier: 1 },
      equation: "\\sin x\\cdot\\operatorname{tg}\\dfrac{x}{2}=1",
      idea: "Zastępujemy tangens połówkowego kąta ilorazem i pilnujemy dziedziny.",
      steps: [
        "\\operatorname{tg}\\dfrac{x}{2}=\\dfrac{\\sin x}{1+\\cos x},\\qquad\\cos x\\neq-1",
        "\\dfrac{\\sin^2x}{1+\\cos x}=1",
        "1-\\cos x=1",
        "\\cos x=0",
        "x=\\dfrac{\\pi}{2}+k\\pi",
      ],
      families: [F(1, 2, 1, 1)],
    },
    {
      validation: { kind: "productCosSum", first: 1, second: 2 },
      equation: "\\sin x\\sin 2x=\\cos x\\cos 2x",
      idea: "Przenosimy składniki na jedną stronę i rozpoznajemy cosinus sumy.",
      steps: [
        "\\cos x\\cos 2x-\\sin x\\sin 2x=0",
        "\\cos 3x=0",
        "3x=\\dfrac{\\pi}{2}+k\\pi",
        "x=\\dfrac{\\pi}{6}+k\\dfrac{\\pi}{3}",
      ],
      families: [F(1, 6, 1, 3)],
    },
  ],
};

const arg = (coefficient) => coefficient === 1 ? "x" : `${coefficient}x`;

function reductionShiftVariant(shift) {
  const anchor = R(shift.d - 2 * shift.n, 4 * shift.d);
  const shifted = shift.n === 0 ? "x" : `\\left(x${shift.n < 0 ? "-" : "+"}${piLatex(R(Math.abs(shift.n), shift.d))}\\right)`;
  return {
    validation: { kind: "reductionShift", shift },
    equation: `\\sin ${shifted}=\\cos x`,
    idea: "Zamieniamy cosinus na sinus kąta dopełniającego, a potem porównujemy argumenty.",
    steps: [
      "\\cos x=\\sin\\left(\\dfrac{\\pi}{2}-x\\right)",
      `${shifted}=\\dfrac{\\pi}{2}-x+2k\\pi`,
      `2x=${piLatex(R(shift.d - 2 * shift.n, 2 * shift.d))}+2k\\pi`,
      `x=${piLatex(anchor)}+k\\pi`,
      `${piLatex(shift)}\\neq\\dfrac{\\pi}{2}+2k\\pi`,
    ],
    families: [{ anchor, period: R(1) }],
  };
}

const COS_TARGETS = {
  one: { coefficient: "1", alpha: R(1, 3) },
  negOne: { coefficient: "-1", alpha: R(2, 3) },
  sqrt2: { coefficient: "\\sqrt2", alpha: R(1, 4) },
  negSqrt2: { coefficient: "-\\sqrt2", alpha: R(3, 4) },
  sqrt3: { coefficient: "\\sqrt3", alpha: R(1, 6) },
  zero: { coefficient: "0", alpha: R(1, 2) },
};

function coefficientTimesFactor(coefficient, factor) {
  if (coefficient === "0") return "0";
  if (coefficient === "1") return factor;
  if (coefficient === "-1") return `-${factor}`;
  return `${coefficient}${factor}`;
}

function subtractCoefficientLatex(coefficient) {
  if (coefficient === "0") return "";
  return coefficient.startsWith("-") ? `+${coefficient.slice(1)}` : `-${coefficient}`;
}

function halfCoefficientLatex(coefficient) {
  if (coefficient === "0") return "0";
  if (coefficient === "1") return "\\dfrac12";
  if (coefficient === "-1") return "-\\dfrac12";
  if (coefficient.startsWith("-")) return `-\\dfrac{${coefficient.slice(1)}}{2}`;
  return `\\dfrac{${coefficient}}{2}`;
}

function sumVariant(fn, middle, difference, targetKey) {
  const first = middle - difference;
  const second = middle + difference;
  const target = COS_TARGETS[targetKey];
  const functionLatex = fn === "sin" ? "\\sin" : "\\cos";
  const middleFactor = `${functionLatex} ${arg(middle)}`;
  const baseFamily = fn === "sin" ? F(0, 1, 1, middle) : F(1, 2 * middle, 1, middle);
  const cosineFamilies = targetKey === "zero"
    ? [F(target.alpha.n, target.alpha.d * difference, 1, difference)]
    : [F(target.alpha.n, target.alpha.d * difference, 2, difference), F(-target.alpha.n, target.alpha.d * difference, 2, difference)];
  return {
    validation: { kind: "sum", fn, middle, difference, targetKey },
    equation: `${functionLatex} ${arg(first)}+${functionLatex} ${arg(second)}=${coefficientTimesFactor(target.coefficient, middleFactor)}`,
    idea: `Zamieniamy sumę ${fn === "sin" ? "sinusów" : "cosinusów"} na iloczyn, korzystając ze średniej arytmetycznej kątów.`,
    steps: [
      `${functionLatex} ${arg(first)}+${functionLatex} ${arg(second)}=2${middleFactor}\\cos ${arg(difference)}`,
      `${middleFactor}\\left(2\\cos ${arg(difference)}${subtractCoefficientLatex(target.coefficient)}\\right)=0`,
      `${middleFactor}=0\\quad\\lor\\quad\\cos ${arg(difference)}=${halfCoefficientLatex(target.coefficient)}`,
    ],
    families: [baseFamily, ...cosineFamilies],
  };
}

function angleAdditionVariant(alpha, direction, beta, rhs) {
  const sign = direction > 0 ? "-" : "+";
  const shifted = direction > 0 ? "+" : "-";
  const firstAnchor = R(-direction * alpha.n * beta.d + beta.n * alpha.d, alpha.d * beta.d);
  const secondAnchor = R(-direction * alpha.n * beta.d - beta.n * alpha.d, alpha.d * beta.d);
  const families = beta.n === 0
    ? [{ anchor: R(-direction * alpha.n, alpha.d), period: R(2, 1) }]
    : [{ anchor: firstAnchor, period: R(2, 1) }, { anchor: secondAnchor, period: R(2, 1) }];
  return {
    validation: { kind: "angleAddition", alpha, direction, beta },
    equation: `\\cos ${piLatex(alpha)}\\cos x${sign}\\sin ${piLatex(alpha)}\\sin x=${rhs}`,
    idea: "Łączymy dwa składniki we wzór na cosinus sumy lub różnicy kątów.",
    steps: [
      `\\cos\\left(x${shifted}${piLatex(alpha)}\\right)=${rhs}`,
      `x${shifted}${piLatex(alpha)}=${beta.n === 0 ? "2k\\pi" : `\\pm${piLatex(beta)}+2k\\pi`}`,
    ],
    families,
  };
}

const powerAdditions = [
  { equation: "\\sin^4x+\\cos^4x=\\dfrac34", idea: "Redukujemy czwarte potęgi do kwadratu sinusa podwójnego kąta.", steps: ["1-\\dfrac12\\sin^2 2x=\\dfrac34", "\\sin^2 2x=\\dfrac12"], families: [F(1, 8, 1, 4)] },
  { equation: "\\sin^4x+\\cos^4x=\\dfrac78", idea: "Redukujemy czwarte potęgi do kwadratu sinusa podwójnego kąta.", steps: ["1-\\dfrac12\\sin^2 2x=\\dfrac78", "\\sin^2 2x=\\dfrac14"], families: [F(1, 12, 1, 2), F(5, 12, 1, 2)] },
  { equation: "\\sin^4x+\\cos^4x=\\dfrac58", idea: "Redukujemy czwarte potęgi do kwadratu sinusa podwójnego kąta.", steps: ["1-\\dfrac12\\sin^2 2x=\\dfrac58", "\\sin^2 2x=\\dfrac34"], families: [F(1, 6, 1, 2), F(1, 3, 1, 2)] },
  { equation: "\\sin^4x-\\cos^4x=-1", idea: "Rozkładamy różnicę czwartych potęg i używamy jedynki trygonometrycznej.", steps: ["-\\cos 2x=-1", "\\cos 2x=1"], families: [F(0, 1, 1, 1)] },
  { equation: "\\sin^4x-\\cos^4x=-\\dfrac12", idea: "Rozkładamy różnicę czwartych potęg i używamy jedynki trygonometrycznej.", steps: ["-\\cos 2x=-\\dfrac12", "\\cos 2x=\\dfrac12"], families: [F(1, 6, 1, 1), F(-1, 6, 1, 1)] },
  { equation: "\\sin^4x-\\cos^4x=0", idea: "Rozkładamy różnicę czwartych potęg i używamy jedynki trygonometrycznej.", steps: ["-\\cos 2x=0", "\\cos 2x=0"], families: [F(1, 4, 1, 2)] },
  { equation: "\\sin^4x-\\cos^4x=\\dfrac12", idea: "Rozkładamy różnicę czwartych potęg i używamy jedynki trygonometrycznej.", steps: ["-\\cos 2x=\\dfrac12", "\\cos 2x=-\\dfrac12"], families: [F(1, 3, 1, 1), F(-1, 3, 1, 1)] },
  { equation: "\\sin^2x-\\cos 2x=2", idea: "Zapisujemy cosinus podwójnego kąta przez kwadrat sinusa.", steps: ["\\cos 2x=1-2\\sin^2x", "3\\sin^2x=3", "\\sin^2x=1"], families: [F(1, 2, 1, 1)] },
];

const halfDoubleAdditions = [
  { equation: "2\\cos x+3=-4\\cos\\dfrac{x}{2}", idea: "Podstawiamy t=cos(x/2) i rozwiązujemy równanie kwadratowe.", steps: ["t=\\cos\\dfrac{x}{2},\\quad\\cos x=2t^2-1", "4t^2+4t+1=0", "(2t+1)^2=0", "t=-\\dfrac12"], families: [F(4, 3, 4, 1), F(-4, 3, 4, 1)] },
  { equation: "\\cos x+2=-2\\sqrt2\\cos\\dfrac{x}{2}", idea: "Podstawiamy t=cos(x/2) i rozwiązujemy równanie kwadratowe.", steps: ["t=\\cos\\dfrac{x}{2},\\quad\\cos x=2t^2-1", "2t^2+2\\sqrt2t+1=0", "(\\sqrt2t+1)^2=0", "t=-\\dfrac{\\sqrt2}{2}"], families: [F(3, 2, 4, 1), F(-3, 2, 4, 1)] },
  { equation: "2\\cos x+5=4\\sqrt3\\cos\\dfrac{x}{2}", idea: "Podstawiamy t=cos(x/2) i rozwiązujemy równanie kwadratowe.", steps: ["t=\\cos\\dfrac{x}{2},\\quad\\cos x=2t^2-1", "4t^2-4\\sqrt3t+3=0", "(2t-\\sqrt3)^2=0", "t=\\dfrac{\\sqrt3}{2}"], families: [F(1, 3, 4, 1), F(-1, 3, 4, 1)] },
  { equation: "2\\cos x+5=-4\\sqrt3\\cos\\dfrac{x}{2}", idea: "Podstawiamy t=cos(x/2) i rozwiązujemy równanie kwadratowe.", steps: ["t=\\cos\\dfrac{x}{2},\\quad\\cos x=2t^2-1", "4t^2+4\\sqrt3t+3=0", "(2t+\\sqrt3)^2=0", "t=-\\dfrac{\\sqrt3}{2}"], families: [F(5, 3, 4, 1), F(-5, 3, 4, 1)] },
  { equation: "\\sin 2x=\\sin x", idea: "Stosujemy wzór na sinus podwójnego kąta i wyłączamy wspólny czynnik.", steps: ["2\\sin x\\cos x=\\sin x", "\\sin x(2\\cos x-1)=0"], families: [F(0, 1, 1, 1), F(1, 3, 2, 1), F(-1, 3, 2, 1)] },
  { equation: "\\cos 2x=\\cos x", idea: "Zapisujemy cosinus podwójnego kąta przez cosinus x.", steps: ["2\\cos^2x-1=\\cos x", "(\\cos x-1)(2\\cos x+1)=0"], families: [F(0, 1, 2, 1), F(2, 3, 2, 1), F(-2, 3, 2, 1)] },
];

function productCosSumVariant(first, second) {
  const sum = first + second;
  return { validation: { kind: "productCosSum", first, second }, equation: `\\sin ${arg(first)}\\sin ${arg(second)}=\\cos ${arg(first)}\\cos ${arg(second)}`, idea: "Przenosimy iloczyny na jedną stronę i rozpoznajemy cosinus sumy kątów.", steps: [`\\cos ${arg(first)}\\cos ${arg(second)}-\\sin ${arg(first)}\\sin ${arg(second)}=0`, `\\cos ${arg(sum)}=0`], families: [F(1, 2 * sum, 1, sum)] };
}

function productSinDiffVariant(first, second) {
  const difference = first - second;
  return { validation: { kind: "productSinDiff", first, second }, equation: `\\sin ${arg(first)}\\cos ${arg(second)}=\\cos ${arg(first)}\\sin ${arg(second)}`, idea: "Przenosimy iloczyny na jedną stronę i rozpoznajemy sinus różnicy kątów.", steps: [`\\sin ${arg(first)}\\cos ${arg(second)}-\\cos ${arg(first)}\\sin ${arg(second)}=0`, `\\sin ${arg(Math.abs(difference))}=0`], families: [F(0, 1, 1, difference)] };
}

function tangentHalfVariant(multiplier) {
  const halfArgument = multiplier === 2 ? "x" : `\\dfrac{${arg(multiplier)}}{2}`;
  return { validation: { kind: "tangentHalf", multiplier }, equation: `\\sin ${arg(multiplier)}\\operatorname{tg}${halfArgument}=1`, idea: "Zastępujemy tangens połówkowego kąta ilorazem i zapisujemy warunek dziedziny.", steps: [`\\operatorname{tg}${halfArgument}=\\dfrac{\\sin ${arg(multiplier)}}{1+\\cos ${arg(multiplier)}}`, `1-\\cos ${arg(multiplier)}=1`, `\\cos ${arg(multiplier)}=0`], families: [F(1, 2 * multiplier, 1, multiplier)] };
}

const CORE_VARIANT_POOLS = {
  reduction: [CONFIGS.reduction[0], ...[R(0), R(1, 6), R(-1, 6), R(1, 4), R(-1, 4), R(1, 3), R(-1, 3), R(2, 3), R(-2, 3)].map(reductionShiftVariant)],
  sumToProduct: [...CONFIGS.sumToProduct, ...[
    ["sin", 2, 1, "sqrt2"], ["sin", 3, 1, "one"], ["sin", 3, 2, "negOne"], ["sin", 4, 1, "zero"], ["sin", 4, 2, "sqrt3"],
    ["cos", 2, 1, "one"], ["cos", 3, 1, "negSqrt2"], ["cos", 3, 2, "zero"], ["cos", 4, 1, "sqrt3"],
    ["cos", 4, 2, "sqrt2"], ["cos", 5, 1, "negOne"], ["sin", 5, 2, "zero"], ["cos", 5, 2, "sqrt3"],
  ].map((spec) => sumVariant(...spec))],
  angleAddition: [...CONFIGS.angleAddition, ...[
    [R(1, 6), 1, R(1, 3), "\\dfrac12"], [R(1, 4), -1, R(1, 4), "\\dfrac{\\sqrt2}{2}"],
    [R(1, 6), 1, R(1, 4), "\\dfrac{\\sqrt2}{2}"], [R(1, 6), -1, R(1, 2), "0"],
    [R(1, 4), 1, R(1, 3), "\\dfrac12"], [R(1, 3), -1, R(1, 6), "\\dfrac{\\sqrt3}{2}"],
    [R(1, 8), -1, R(1, 4), "\\dfrac{\\sqrt2}{2}"], [R(3, 8), 1, R(1, 3), "\\dfrac12"],
  ].map((spec) => angleAdditionVariant(...spec))],
  powers: [...CONFIGS.powers, ...powerAdditions],
  halfDouble: [...CONFIGS.halfDouble, ...halfDoubleAdditions],
  product: [...CONFIGS.product, productCosSumVariant(1, 3), productCosSumVariant(2, 3), productCosSumVariant(1, 4), productCosSumVariant(2, 5), productSinDiffVariant(3, 1), productSinDiffVariant(5, 2), tangentHalfVariant(2), tangentHalfVariant(3)],
};

function familyContainedIn(inner, outer) {
  const periodRatio = R(inner.period.n * outer.period.d, inner.period.d * outer.period.n);
  const anchorDifference = R(inner.anchor.n * outer.anchor.d - outer.anchor.n * inner.anchor.d, inner.anchor.d * outer.anchor.d);
  const offsetRatio = R(anchorDifference.n * outer.period.d, anchorDifference.d * outer.period.n);
  return periodRatio.d === 1 && offsetRatio.d === 1;
}

function removeRedundantFamilies(families) {
  return families.filter((family, index) => !families.some((other, otherIndex) =>
    index !== otherIndex && familyContainedIn(family, other) &&
    (!familyContainedIn(other, family) || otherIndex < index)));
}

// Każdy wariant ćwiczy jedną metodę, bez dodatkowego podstawienia na cały argument.
// Rodzina zawarta w innej nie jest osobną odpowiedzią do wpisania.
const VARIANT_POOLS = Object.fromEntries(Object.entries(CORE_VARIANT_POOLS).map(([method, variants]) => [
  method, variants.map((config) => ({ ...config, families: removeRedundantFamilies(config.families) })),
]));

export const coreVariantCounts = Object.fromEntries(Object.entries(CORE_VARIANT_POOLS).map(([method, variants]) => [method, variants.length]));
export const variantCounts = Object.fromEntries(Object.entries(VARIANT_POOLS).map(([method, variants]) => [method, variants.length]));
export const advancedVariantPools = VARIANT_POOLS;

const pick = (items) => items[Math.floor(Math.random() * items.length)];
const ratValue = ({ n, d }) => n / d;
const addRat = (left, right) => R(left.n * right.d + right.n * left.d, left.d * right.d);
const multiplyRat = (value, integer) => R(value.n * integer, value.d);
const ratKey = (value) => `${value.n}/${value.d}`;

const INTERVALS = [R(1), R(3, 2), R(2)].flatMap((length) =>
  Array.from({ length: 13 }, (_, index) => {
    const left = R(index - 8, 4);
    return [left, addRat(left, length)];
  })
);

function solutionsInInterval(families, interval) {
  const [left, right] = interval;
  const found = new Map();
  families.forEach(({ anchor, period }) => {
    for (let k = -80; k <= 80; k += 1) {
      const value = addRat(anchor, multiplyRat(period, k));
      const numeric = ratValue(value);
      if (numeric >= ratValue(left) - 1e-9 && numeric <= ratValue(right) + 1e-9) found.set(ratKey(value), value);
    }
  });
  return [...found.values()].sort((a, b) => ratValue(a) - ratValue(b));
}

export function generateTask({ method = "reduction" } = {}) {
  const config = pick(VARIANT_POOLS[method] || VARIANT_POOLS.reduction);
  return { type, data: { ...config, method, maxPoints: 2 } };
}

function intervalCandidatesFor(methods) {
  const candidates = [];
  methods.forEach((method) => {
    (VARIANT_POOLS[method] || []).forEach((config) => {
      INTERVALS.forEach((interval) => {
        const expected = solutionsInInterval(config.families, interval);
        if (expected.length >= 3 && expected.length <= 5) candidates.push({ method, config, interval, expected });
      });
    });
  });
  return candidates;
}

export function intervalCandidateCount(methods = METHODS.map(({ value }) => value)) {
  return intervalCandidatesFor(methods).length;
}

export function generateIntervalTask({ methods = METHODS.map(({ value }) => value) } = {}) {
  const candidates = intervalCandidatesFor(methods);
  const selected = pick(candidates);
  if (!selected) return generateTask({ method: pick(methods) });
  return { type, data: { ...selected.config, method: selected.method, interval: selected.interval, expected: selected.expected, maxPoints: 3 } };
}

function piLatex({ n, d }) {
  if (n === 0) return "0";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (d === 1) return abs === 1 ? `${sign}\\pi` : `${sign}${abs}\\pi`;
  return `${sign}\\dfrac{${abs === 1 ? "\\pi" : `${abs}\\pi`}}{${d}}`;
}

function familyLatexFor({ anchor, period }, variable = "x") {
  if (anchor.n === 0) return `${variable}=k\\cdot${piLatex(period)}`;
  return `${variable}=${piLatex(anchor)}+k\\cdot${piLatex(period)}`;
}

const familyLatex = (family) => familyLatexFor(family, "x");
const familiesLatex = (families) => families.map(familyLatex).join("\\qquad\\lor\\qquad{}");
const familiesLatexFor = (families, variable) => families.map((family) => familyLatexFor(family, variable)).join("\\qquad\\lor\\qquad{}");
const solutionSetLatex = (values) => `\\left\\{${values.map(piLatex).join(",\\;")}\\right\\}`;
const intervalLatex = ([left, right]) => `\\left[${piLatex(left)},${piLatex(right)}\\right]`;

export function renderTask({ data }) {
  return <div style={{ lineHeight: 1.7 }}><p>{data.interval ? <>Rozwiąż równanie w przedziale <InlineMath math={intervalLatex(data.interval)} />.</> : "Rozwiąż równanie."}</p><BlockMath math={data.equation} /></div>;
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const value = userAnswer || {};
  const families = Array.isArray(value.families) ? value.families : [];
  const xs = Array.isArray(value.xs) ? value.xs : [];
  const count = Number.isInteger(value.familyCount) ? value.familyCount : 0;
  const setFamily = (index, nextValue) => { const next = [...families]; next[index] = nextValue; onChange?.({ ...value, families: next }); };
  const setX = (index, nextValue) => { const next = [...xs]; next[index] = nextValue; onChange?.({ ...value, xs: next }); };
  const solutionCount = Number.isInteger(value.solutionCount) ? value.solutionCount : 0;
  return <div style={{ display: "grid", gap: 20 }}>
    <div style={{ display: "grid", gap: 7 }}>
      <div><strong>Krok 1 (1 pkt).</strong> Wybierz główną metodę przekształcenia.</div>
      <div style={{ maxWidth: 620 }}><SelectInput value={value.method || ""} onChange={(event) => onChange?.({ ...value, method: event.target.value })} disabled={disabled} options={METHODS} /></div>
    </div>
    <div style={{ display: "grid", gap: 10 }}>
      <div><strong>Krok 2 (1 pkt).</strong> Podaj wszystkie rodziny rozwiązań. Warunek <InlineMath math={"k\\in\\mathbb Z"} /> pojawi się w podglądzie automatycznie.</div>
      <div style={{ maxWidth: 260 }}><div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Wybierz liczbę rodzin rozwiązań:</div><SelectInput value={count || ""} onChange={(event) => onChange?.({ ...value, familyCount: event.target.value === "" ? "" : Number(event.target.value) })} disabled={disabled} options={[1, 2, 3].map((number) => ({ value: number, label: String(number) }))} /></div>
      {Array.from({ length: count }).map((_, index) => <div key={index} style={{ display: "grid", gap: 5 }}><div><InlineMath math={`x_${index + 1}=`} /></div><PiInput value={families[index]} onChange={(nextValue) => setFamily(index, nextValue)} disabled={disabled} label={`x_${index + 1}`} placeholder="np. pi/3+k*pi" allowAffine affineSetPreview /></div>)}
    </div>
    {data.interval && <div style={{ display: "grid", gap: 10 }}>
      <div><strong>Krok 3 (1 pkt).</strong> Wybierz z rozwiązania ogólnego wszystkie wartości należące do <InlineMath math={intervalLatex(data.interval)} />. Kolejność nie ma znaczenia.</div>
      <div style={{ maxWidth: 260 }}><div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Wybierz liczbę rozwiązań:</div><SelectInput value={solutionCount || ""} onChange={(event) => onChange?.({ ...value, solutionCount: event.target.value === "" ? "" : Number(event.target.value) })} disabled={disabled} options={[1, 2, 3, 4, 5].map((number) => ({ value: number, label: String(number) }))} /></div>
      {Array.from({ length: solutionCount }).map((_, index) => <div key={index} style={{ display: "grid", gap: 5 }}><div><InlineMath math={`x_${index + 1}=`} /></div><PiInput value={xs[index]} onChange={(nextValue) => setX(index, nextValue)} disabled={disabled} label={`x_${index + 1}`} placeholder="np. -pi/2" /></div>)}
    </div>}
  </div>;
}

function familyEquivalent(actual, expected) {
  const parsed = actual?.parsed;
  if (!parsed || !Number.isFinite(parsed.a) || !Number.isFinite(parsed.b)) return false;
  const anchor = ratValue(expected.anchor) * Math.PI;
  const period = Math.abs(ratValue(expected.period) * Math.PI);
  if (Math.abs(Math.abs(parsed.b) - period) > 1e-6) return false;
  const turns = (parsed.a - anchor) / period;
  return Math.abs(turns - Math.round(turns)) < 1e-6;
}

function rationalPi(value) {
  if (!Number.isFinite(value)) return null;
  const coefficient = value / Math.PI;
  for (let denominator = 1; denominator <= 1000; denominator += 1) {
    const numerator = Math.round(coefficient * denominator);
    if (Math.abs(coefficient - numerator / denominator) < 1e-8) return R(numerator, denominator);
  }
  return null;
}

const lcm = (left, right) => Math.abs(left / gcd(left, right) * right);
const positiveMod = (value, modulus) => ((value % modulus) + modulus) % modulus;

function familyResidues(family, scale, commonPeriod) {
  const anchor = family.anchor.n * (scale / family.anchor.d);
  const period = Math.abs(family.period.n * (scale / family.period.d));
  if (!Number.isSafeInteger(anchor) || !Number.isSafeInteger(period) || period === 0) return null;
  const residues = new Set();
  for (let residue = positiveMod(anchor, period); residue < commonPeriod; residue += period) residues.add(residue);
  return residues;
}

function equivalentSolutionSets(answers, expected) {
  const actual = answers.map(({ parsed }) => parsed && ({ anchor: rationalPi(parsed.a), period: rationalPi(parsed.b) }));
  if (actual.some((family) => !family?.anchor || !family.period || family.period.n === 0)) return false;
  const all = [...actual, ...expected];
  const scale = all.flatMap(({ anchor, period }) => [anchor.d, period.d]).reduce(lcm, 1);
  if (!Number.isSafeInteger(scale) || scale > 10000) return false;
  const commonPeriod = all.map(({ period }) => Math.abs(period.n * (scale / period.d))).reduce(lcm, 1);
  if (!Number.isSafeInteger(commonPeriod) || commonPeriod > 50000) return false;
  const actualResidues = actual.map((family) => familyResidues(family, scale, commonPeriod));
  const expectedResidues = expected.map((family) => familyResidues(family, scale, commonPeriod));
  if ([...actualResidues, ...expectedResidues].some((residues) => !residues)) return false;
  // Nie zaliczamy rodzin powtórzonych ani takich, które nic nie dodają do odpowiedzi.
  if (actualResidues.some((residues, index) => [...residues].every((value) => actualResidues.some((other, otherIndex) => otherIndex !== index && other.has(value))))) return false;
  const actualUnion = new Set(actualResidues.flatMap((residues) => [...residues]));
  const expectedUnion = new Set(expectedResidues.flatMap((residues) => [...residues]));
  return actualUnion.size === expectedUnion.size && [...actualUnion].every((value) => expectedUnion.has(value));
}

function checkFamilies(data, userAnswer) {
  if (!Number.isInteger(userAnswer?.familyCount) || userAnswer.familyCount < 1 || userAnswer.familyCount > 3) return false;
  const answers = Array.isArray(userAnswer?.families) ? userAnswer.families.slice(0, userAnswer.familyCount) : [];
  if (answers.length !== userAnswer.familyCount) return false;
  if (answers.length !== data.families.length) return equivalentSolutionSets(answers, data.families);
  const used = new Set();
  const matchingFamilies = data.families.every((expected) => {
    const index = answers.findIndex((answer, answerIndex) => !used.has(answerIndex) && familyEquivalent(answer, expected));
    if (index < 0) return false;
    used.add(index);
    return true;
  });
  return matchingFamilies || equivalentSolutionSets(answers, data.families);
}

function parsedPiValue(value) {
  if (value && typeof value === "object" && Number.isFinite(value.parsed)) return value.parsed;
  const raw = value && typeof value === "object" ? value.raw : value;
  return parsePiLikeInput(raw);
}

function checkIntervalSolutions(data, userAnswer) {
  if (!data.interval || !Array.isArray(data.expected)) return true;
  if (userAnswer?.solutionCount !== data.expected.length) return false;
  const values = Array.isArray(userAnswer?.xs) ? userAnswer.xs.slice(0, userAnswer.solutionCount).map(parsedPiValue) : [];
  if (values.length !== data.expected.length || values.some((value) => !Number.isFinite(value))) return false;
  const actual = [...values].sort((a, b) => a - b);
  const expected = data.expected.map((value) => ratValue(value) * Math.PI).sort((a, b) => a - b);
  return expected.every((value, index) => Math.abs(value - actual[index]) < 1e-6);
}

export function checkAnswer({ data }, userAnswer = {}) {
  const methodOk = userAnswer.method === data.method;
  const familiesOk = checkFamilies(data, userAnswer);
  const intervalOk = checkIntervalSolutions(data, userAnswer);
  const maxScore = data.interval ? 3 : 2;
  const score = Number(methodOk) + Number(familiesOk) + (data.interval ? Number(intervalOk) : 0);
  const parts = { "dobór metody": methodOk, "rozwiązanie ogólne": familiesOk };
  if (data.interval) parts["rozwiązania z przedziału"] = intervalOk;
  return { correct: score === maxScore, isCorrect: score === maxScore, score, maxScore, parts, message: score === maxScore ? "Dobrze!" : "Sprawdź dobór wzoru, wszystkie rodziny rozwiązań oraz granice przedziału." };
}

export function renderAnswer({ data }) {
  const method = METHODS.find((item) => item.value === data.method)?.label;
  return <div style={{ display: "grid", gap: 6 }}><div><strong>Metoda:</strong> {method}</div><div><strong>Rozwiązanie ogólne:</strong> <InlineMath math={`${familiesLatex(data.families)},\\qquad k\\in\\mathbb Z`} /></div>{data.interval && <div><strong>W podanym przedziale:</strong> <InlineMath math={`x\\in${solutionSetLatex(data.expected)}`} /></div>}</div>;
}

function specialAngleValuesLatex(alpha) {
  const values = {
    "1/6": ["\\dfrac{\\sqrt3}{2}", "\\dfrac12"],
    "1/4": ["\\dfrac{\\sqrt2}{2}", "\\dfrac{\\sqrt2}{2}"],
    "1/3": ["\\dfrac12", "\\dfrac{\\sqrt3}{2}"],
  }[ratKey(alpha)];
  return values ? `\\cos${piLatex(alpha)}=${values[0]},\\qquad\\sin${piLatex(alpha)}=${values[1]}` : null;
}

function MethodPreparation({ data }) {
  const methodEquation = data.sourceConfig?.equation || data.equation;
  if (data.method === "reduction") return <div>
    <p>Chcemy mieć tę samą funkcję po obu stronach. Korzystamy z zależności dla kątów dopełniających:</p>
    <BlockMath math={"\\sin\\alpha=\\cos\\left(\\dfrac{\\pi}{2}-\\alpha\\right),\\qquad\\cos\\alpha=\\sin\\left(\\dfrac{\\pi}{2}-\\alpha\\right)"} />
    <p>Po zamianie sprawdzamy oba przypadki równości funkcji. Jeśli drugi daje sprzeczność, zapisujemy to wprost; nie zakładamy z góry, że istnieją dwie rodziny.</p>
  </div>;
  if (data.method === "sumToProduct") return <div>
    <p>Kąty w sumie są symetryczne względem ich średniej. Dlatego obliczamy połowę sumy i połowę różnicy kątów:</p>
    <BlockMath math={"\\sin A+\\sin B=2\\sin\\dfrac{A+B}{2}\\cos\\dfrac{A-B}{2}"} />
    <BlockMath math={"\\cos A+\\cos B=2\\cos\\dfrac{A+B}{2}\\cos\\dfrac{A-B}{2}"} />
    {data.validation?.kind === "sum" && <BlockMath math={`A=${arg(data.validation.middle + data.validation.difference)},\\quad B=${arg(data.validation.middle - data.validation.difference)},\\quad\\dfrac{A+B}{2}=${arg(data.validation.middle)},\\quad\\dfrac{A-B}{2}=${arg(data.validation.difference)}`} />}
    <p>Po przeniesieniu prawej strony wyłączamy wspólną funkcję przed nawias. Nie dzielimy przez nią, ponieważ jej miejsca zerowe także są rozwiązaniami.</p>
  </div>;
  if (data.method === "angleAddition") return <div>
    <p>Współczynniki przy sinusie i cosinusie są wartościami tego samego kąta. Rozpoznajemy więc jeden z dwóch wzorów:</p>
    <BlockMath math={"\\cos(x+\\alpha)=\\cos x\\cos\\alpha-\\sin x\\sin\\alpha"} />
    <BlockMath math={"\\cos(x-\\alpha)=\\cos x\\cos\\alpha+\\sin x\\sin\\alpha"} />
    {data.validation?.kind === "angleAddition" && <p>W tym zadaniu <InlineMath math={`\\alpha=${piLatex(data.validation.alpha)}`} />. Podstawiamy ten kąt do obu miejsc we wzorze i dopiero wtedy łączymy składniki.</p>}
    {data.validation?.kind === "angleAddition" && specialAngleValuesLatex(data.validation.alpha) && <BlockMath math={specialAngleValuesLatex(data.validation.alpha)} />}
    <p>Dwa składniki zostają zastąpione jedną funkcją, którą rozwiązujemy tak jak w części pierwszej.</p>
  </div>;
  if (data.method === "powers") {
    if (methodEquation.includes("\\sin^4x+\\cos^4x")) return <div>
      <p>Sumę czwartych potęg tworzymy ze wzoru na kwadrat sumy:</p>
      <BlockMath math={"\\sin^4x+\\cos^4x=(\\sin^2x+\\cos^2x)^2-2\\sin^2x\\cos^2x"} />
      <p>Wstawiamy <InlineMath math={"\\sin^2x+\\cos^2x=1"} /> oraz <InlineMath math={"\\sin 2x=2\\sin x\\cos x"} />:</p>
      <BlockMath math={"\\sin^4x+\\cos^4x=1-\\dfrac12\\sin^2 2x"} />
    </div>;
    if (methodEquation.includes("\\sin^4x") && methodEquation.includes("\\cos^4x")) return <div>
      <p>Najpierw stosujemy różnicę kwadratów. Czwarta potęga to kwadrat drugiej potęgi:</p>
      <BlockMath math={"\\sin^4x-\\cos^4x=(\\sin^2x-\\cos^2x)(\\sin^2x+\\cos^2x)"} />
      <p>Drugi nawias jest równy jeden, a pierwszy zapisujemy za pomocą cosinusa podwójnego kąta:</p>
      <BlockMath math={"\\sin^2x+\\cos^2x=1,\\qquad\\sin^2x-\\cos^2x=-\\cos 2x"} />
      <BlockMath math={"\\sin^4x-\\cos^4x=-\\cos 2x"} />
    </div>;
    return <div><p>Wybieramy postać wzoru na podwójny kąt, która zawiera tę samą funkcję co pozostała część równania:</p><BlockMath math={"\\cos 2x=1-2\\sin^2x=2\\cos^2x-1"} /></div>;
  }
  if (data.method === "halfDouble") {
    if (methodEquation.includes("\\dfrac{x}{2}")) return <div>
      <p>W równaniu powtarza się <InlineMath math={"\\cos\\frac{x}{2}"} />. Wprowadzamy niewiadomą pomocniczą i wyrażamy przez nią także <InlineMath math={"\\cos x"} />:</p>
      <BlockMath math={"t=\\cos\\dfrac{x}{2},\\qquad\\cos x=2t^2-1"} />
      <p>Otrzymujemy zwykłe równanie kwadratowe względem <InlineMath math={"t"} />. Po jego rozwiązaniu wracamy do zmiennej <InlineMath math={"x"} />.</p>
    </div>;
    return <div><p>Rozwijamy funkcję podwójnego kąta w postaci dopasowanej do drugiej strony:</p><BlockMath math={"\\sin 2x=2\\sin x\\cos x"} /><BlockMath math={"\\cos 2x=1-2\\sin^2x=2\\cos^2x-1"} /><p>Następnie przenosimy wszystko na jedną stronę i rozkładamy wyrażenie na czynniki.</p></div>;
  }
  if (methodEquation.includes("\\operatorname{tg}")) return <div>
    <p>Najpierw zapisujemy dziedzinę, ponieważ tangens połówkowego kąta nie istnieje dla wszystkich wartości:</p>
    <BlockMath math={"\\operatorname{tg}\\dfrac{u}{2}=\\dfrac{\\sin u}{1+\\cos u},\\qquad1+\\cos u\\ne0"} />
    <p>Dopiero potem skracamy wyrażenie, korzystając z rozkładu <InlineMath math={"\\sin^2u=(1-\\cos u)(1+\\cos u)"} />.</p>
  </div>;
  return <div>
    <p>Przenosimy iloczyny na jedną stronę i rozpoznajemy wzór na funkcję sumy albo różnicy kątów:</p>
    <BlockMath math={"\\cos(A+B)=\\cos A\\cos B-\\sin A\\sin B"} />
    <BlockMath math={"\\sin(A-B)=\\sin A\\cos B-\\cos A\\sin B"} />
  </div>;
}

const STEP_DESCRIPTIONS = {
  reduction: ["Wykonujemy zamianę funkcji.", "Rozpatrujemy pierwszy dopuszczalny układ argumentów.", "Rozpatrujemy drugi układ argumentów.", "Wyznaczamy x i upraszczamy okres."],
  sumToProduct: ["Stosujemy wzór do sumy po lewej stronie.", "Przenosimy prawą stronę i wyłączamy wspólny czynnik.", "Korzystamy z zasady iloczynu równego zeru."],
  angleAddition: ["Łączymy dwa składniki w jedną funkcję.", "Zapisujemy wszystkie przypadki równania podstawowego.", "Wyznaczamy x w każdej gałęzi."],
  powers: ["Podstawiamy odpowiednią tożsamość i porządkujemy równanie.", "Sprowadzamy równanie do jednej funkcji trygonometrycznej.", "Rozwiązujemy otrzymane równanie podstawowe.", "Upraszczamy rodziny rozwiązań."],
  halfDouble: ["Wykonujemy podstawienie albo rozwijamy funkcję podwójnego kąta.", "Rozwiązujemy otrzymane równanie algebraiczne.", "Wracamy do funkcji trygonometrycznej.", "Wyznaczamy wszystkie rodziny x."],
  product: ["Stosujemy wybraną tożsamość do iloczynów.", "Upraszczamy równanie do jednej funkcji.", "Rozwiązujemy równanie podstawowe.", "Sprawdzamy otrzymane wartości z dziedziną."],
};

const HALF_ANGLE_DESCRIPTIONS = [
  "Podstawiamy t i zapisujemy cosinus całego kąta przez t.",
  "Przenosimy wszystkie wyrazy na jedną stronę równania kwadratowego.",
  "Rozpoznajemy kwadrat dwumianu.",
  "Wyznaczamy t, a następnie wracamy do równania z cosinusem.",
];

const BASIC_TARGETS = {
  zero: { value: 0, latex: "0", sin: [F(0, 1, 1, 1)], cos: [F(1, 2, 1, 1)] },
  one: { value: 1, latex: "1", sin: [F(1, 2, 2, 1)], cos: [F(0, 1, 2, 1)] },
  negOne: { value: -1, latex: "-1", sin: [F(-1, 2, 2, 1)], cos: [F(1, 1, 2, 1)] },
  half: { value: 0.5, latex: "\\dfrac12", sin: [F(1, 6, 2, 1), F(5, 6, 2, 1)], cos: [F(-1, 3, 2, 1), F(1, 3, 2, 1)] },
  negHalf: { value: -0.5, latex: "-\\dfrac12", sin: [F(-1, 6, 2, 1), F(-5, 6, 2, 1)], cos: [F(-2, 3, 2, 1), F(2, 3, 2, 1)] },
  sqrt2Half: { value: Math.sqrt(2) / 2, latex: "\\dfrac{\\sqrt2}{2}", sin: [F(1, 4, 2, 1), F(3, 4, 2, 1)], cos: [F(-1, 4, 2, 1), F(1, 4, 2, 1)] },
  negSqrt2Half: { value: -Math.sqrt(2) / 2, latex: "-\\dfrac{\\sqrt2}{2}", sin: [F(-1, 4, 2, 1), F(-3, 4, 2, 1)], cos: [F(-3, 4, 2, 1), F(3, 4, 2, 1)] },
  sqrt3Half: { value: Math.sqrt(3) / 2, latex: "\\dfrac{\\sqrt3}{2}", sin: [F(1, 3, 2, 1), F(2, 3, 2, 1)], cos: [F(-1, 6, 2, 1), F(1, 6, 2, 1)] },
  negSqrt3Half: { value: -Math.sqrt(3) / 2, latex: "-\\dfrac{\\sqrt3}{2}", sin: [F(-1, 3, 2, 1), F(-2, 3, 2, 1)], cos: [F(-5, 6, 2, 1), F(5, 6, 2, 1)] },
};

const subtractRat = (left, right) => R(left.n * right.d - right.n * left.d, left.d * right.d);
const divideRat = (left, right) => R(left.n * right.d, left.d * right.n);
const scaleRat = (value, multiplier) => R(value.n * multiplier, value.d);

function coefficientX({ n, d }) {
  if (d === 1) return n === 1 ? "x" : `${n}x`;
  return n === 1 ? `\\dfrac{x}{${d}}` : `\\dfrac{${n}x}{${d}}`;
}

function affineArgument(a, b) {
  const xPart = coefficientX(a);
  if (b.n === 0) return xPart;
  const absolute = piLatex(R(Math.abs(b.n), b.d));
  return `${xPart}${b.n < 0 ? "-" : "+"}${absolute}`;
}

function makeBasicCase(fn, targetKey, a = R(1), b = R(0), argumentLatex = null) {
  const target = BASIC_TARGETS[targetKey];
  const baseFamilies = target?.[fn] || [];
  const solutionFamilies = baseFamilies.map(({ anchor, period }) => ({
    anchor: divideRat(subtractRat(anchor, b), a),
    period: (() => { const divided = divideRat(period, a); return R(Math.abs(divided.n), divided.d); })(),
  }));
  return { fn, targetKey, target: target.value, targetLatex: target.latex, baseFamilies, solutionFamilies, a, b, argumentLatex: argumentLatex || affineArgument(a, b) };
}

const SUM_TARGET_KEYS = { one: "half", negOne: "negHalf", sqrt2: "sqrt2Half", negSqrt2: "negSqrt2Half", sqrt3: "sqrt3Half", zero: "zero" };
function angleTargetKey(beta) { return ({ "0/1": "one", "1/6": "sqrt3Half", "1/4": "sqrt2Half", "1/3": "half", "1/2": "zero" })[ratKey(beta)]; }

function powerBasicCases(equation) {
  const sinSquaredCases = {
    "\\sin^4x+\\cos^4x=\\dfrac12": ["one", "negOne"],
    "\\sin^4x+\\cos^4x=\\dfrac34": ["sqrt2Half", "negSqrt2Half"],
    "\\sin^4x+\\cos^4x=\\dfrac78": ["half", "negHalf"],
    "\\sin^4x+\\cos^4x=\\dfrac58": ["sqrt3Half", "negSqrt3Half"],
  };
  if (sinSquaredCases[equation]) return sinSquaredCases[equation].map((target) => makeBasicCase("sin", target, R(2)));
  const cosineCases = {
    "\\sin^4x=1+\\cos^4x": "negOne",
    "\\sin^4x-\\cos^4x=-1": "one",
    "\\sin^4x-\\cos^4x=-\\dfrac12": "half",
    "\\sin^4x-\\cos^4x=0": "zero",
    "\\sin^4x-\\cos^4x=\\dfrac12": "negHalf",
  };
  if (cosineCases[equation]) return [makeBasicCase("cos", cosineCases[equation], R(2))];
  if (equation === "\\sin^2x-\\cos 2x=2") return [makeBasicCase("sin", "one"), makeBasicCase("sin", "negOne")];
  return [];
}

function halfDoubleBasicCases(equation) {
  const halfAngleCases = {
    "2\\cos^2\\dfrac{x}{2}+1=2\\sqrt2\\cos\\dfrac{x}{2}": "sqrt2Half",
    "2\\cos x+3=4\\cos\\dfrac{x}{2}": "half",
    "2\\cos x+3=-4\\cos\\dfrac{x}{2}": "negHalf",
    "\\cos x+2=-2\\sqrt2\\cos\\dfrac{x}{2}": "negSqrt2Half",
    "2\\cos x+5=4\\sqrt3\\cos\\dfrac{x}{2}": "sqrt3Half",
    "2\\cos x+5=-4\\sqrt3\\cos\\dfrac{x}{2}": "negSqrt3Half",
  };
  if (halfAngleCases[equation]) return [makeBasicCase("cos", halfAngleCases[equation], R(1, 2), R(0), "\\dfrac{x}{2}")];
  if (equation === "\\sin 2x=\\sin x") return [makeBasicCase("sin", "zero"), makeBasicCase("cos", "half")];
  if (equation === "\\cos 2x=\\cos x") return [makeBasicCase("cos", "one"), makeBasicCase("cos", "negHalf")];
  return [];
}

export function basicCasesFor(data) {
  if (data.outerAffine && data.sourceConfig) {
    const sourceCases = basicCasesFor({ ...data.sourceConfig, method: data.method });
    return sourceCases.map((item) => {
      const a = R(item.a.n * data.outerAffine.a.n, item.a.d * data.outerAffine.a.d);
      const b = addRat(R(item.a.n * data.outerAffine.b.n, item.a.d * data.outerAffine.b.d), item.b);
      return makeBasicCase(item.fn, item.targetKey, a, b);
    });
  }
  const validation = data.validation || {};
  if (data.method === "reduction" && validation.kind === "reductionSpecial") return [makeBasicCase("sin", "zero", R(1), R(-1, 3))];
  if (data.method === "reduction" && validation.kind === "reductionShift") {
    const shift = validation.shift;
    const anchor = R(shift.d - 2 * shift.n, 4 * shift.d);
    return [makeBasicCase("sin", "zero", R(1), R(-anchor.n, anchor.d))];
  }
  if (data.method === "reduction" && validation.kind === "reduction") {
    const sum = validation.sineCoefficient + validation.cosineCoefficient;
    const difference = validation.sineCoefficient - validation.cosineCoefficient;
    return [
      makeBasicCase("sin", "zero", R(sum, 2), R(-1, 4)),
      ...(difference === 0 ? [] : [makeBasicCase("sin", "zero", R(difference, 2), R(-1, 4))]),
    ];
  }
  if (data.method === "sumToProduct" && validation.kind === "sum") return [makeBasicCase(validation.fn, "zero", R(validation.middle)), makeBasicCase("cos", SUM_TARGET_KEYS[validation.targetKey], R(validation.difference))];
  if (data.method === "angleAddition" && validation.kind === "angleAddition") return [makeBasicCase("cos", angleTargetKey(validation.beta), R(1), scaleRat(validation.alpha, validation.direction))];
  if (data.method === "powers") return powerBasicCases(data.equation);
  if (data.method === "halfDouble") return halfDoubleBasicCases(data.equation);
  if (data.method === "product") {
    if (validation.kind === "productCosSum") return [makeBasicCase("cos", "zero", R(validation.first + validation.second))];
    if (validation.kind === "productSinDiff") return [makeBasicCase("sin", "zero", R(Math.abs(validation.first - validation.second)))];
    if (validation.kind === "tangentHalf") return [makeBasicCase("cos", "zero", R(validation.multiplier))];
  }
  return [];
}

export function intervalSelections(data) {
  if (!data.interval) return [];
  const [left, right] = data.interval;
  return data.families.map((family) => {
    const rows = [];
    for (let k = -80; k <= 80; k += 1) {
      const value = addRat(family.anchor, multiplyRat(family.period, k));
      const numeric = ratValue(value);
      if (numeric >= ratValue(left) - 1e-9 && numeric <= ratValue(right) + 1e-9) rows.push({ k, value });
    }
    return { family, rows };
  });
}

export function intervalSubstitutionLatex({ k, value }) {
  return `k=${k}\\Longrightarrow x=${piLatex(value)}`;
}

function IntervalSelectionExplanation({ data }) {
  const selections = intervalSelections(data);
  return <div>
    <p><strong>Wybór rozwiązań z przedziału.</strong> Dla każdej rodziny wyznaczamy wszystkie całkowite wartości <InlineMath math={"k"} />, dla których wynik należy do <InlineMath math={intervalLatex(data.interval)} />.</p>
    {selections.map(({ family, rows }, index) => <div key={`${ratKey(family.anchor)}-${ratKey(family.period)}-${index}`} style={{ margin: "14px 0" }}>
      <p><strong>Rodzina {index + 1}:</strong></p>
      <BlockMath math={`${familyLatex(family)},\\qquad k\\in\\left\\{${rows.map(({ k }) => k).join(",") || "\\varnothing"}\\right\\}`} />
      {rows.length > 0 && <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "8px 24px" }}>
        {rows.map((row) => <InlineMath key={`${row.k}-${ratKey(row.value)}`} math={intervalSubstitutionLatex(row)} />)}
      </div>}
    </div>)}
    <p>Po zebraniu wyników i usunięciu ewentualnych powtórzeń otrzymujemy wszystkie rozwiązania z przedziału:</p>
    <BlockMath math={`x\\in${solutionSetLatex(data.expected)}`} />
  </div>;
}

function functionName(fn) { return fn === "sin" ? "sinusa" : "cosinusa"; }
function functionLatex(fn) { return fn === "sin" ? "\\sin" : "\\cos"; }
export function basicEquationLatex(item) { return `${functionLatex(item.fn)} u=${item.targetLatex}`; }

function BasicEquationResolution({ data }) {
  const cases = basicCasesFor(data);
  if (!cases.length) return <div><p><strong>Końcowe rozwiązanie równań.</strong> Po zastosowaniu wzoru redukcyjnego otrzymaliśmy wyżej równania liniowe dla argumentów. Rozwiązujemy każde z nich względem <InlineMath math={"x"} />, nie gubiąc składnika z <InlineMath math={"k"} /> ani wynikającego z niego okresu.</p><BlockMath math={`${familiesLatex(data.families)},\\qquad k\\in\\mathbb Z`} /></div>;
  return <div>
    <p><strong>Etap końcowy — równania z części pierwszej.</strong> Po przekształceniach zostały równania z jedną funkcją trygonometryczną. Każde rozwiązujemy osobno: najpierw odczytujemy kąty bazowe z nieprzekształconego wykresu, a dopiero potem rozwiązujemy równanie dla właściwego argumentu.</p>
    {cases.map((item, index) => {
      const baseEquation = basicEquationLatex(item);
      const argumentFamilies = item.baseFamilies.map((family) => `${item.argumentLatex}=${familyLatexFor(family, "u").slice(2)}`).join("\\qquad\\lor\\qquad{}");
      return <div key={`${item.fn}-${item.argumentLatex}-${item.targetLatex}-${index}`} style={{ margin: "18px 0", padding: "12px 14px", borderLeft: "3px solid color-mix(in srgb, var(--color-accent) 70%, #7c3aed)" }}>
        <p><strong>{cases.length > 1 ? `Przypadek ${index + 1}.` : "Równanie podstawowe."}</strong> Oznaczamy cały argument przez <InlineMath math={"u"} />:</p>
        <BlockMath math={`u=${item.argumentLatex},\\qquad ${baseEquation}`} />
        <p>Z wykresu {functionName(item.fn)} na przedziale od <InlineMath math={"-\\pi"} /> do <InlineMath math={"\\pi"} /> odczytujemy wszystkie kąty bazowe odpowiadające prawej stronie.</p>
        <TrigEquationGraph fn={item.fn} target={item.target} families={item.baseFamilies} />
        <p>Po dopisaniu okresu funkcji otrzymujemy:</p>
        <BlockMath math={`${familiesLatexFor(item.baseFamilies, "u")},\\qquad k\\in\\mathbb Z`} />
        <p>Wracamy do argumentu z równania i zapisujemy każdy przypadek:</p>
        <BlockMath math={argumentFamilies} />
        <p>Rozwiązujemy te równania względem <InlineMath math={"x"} />:</p>
        <BlockMath math={`${familiesLatex(item.solutionFamilies)},\\qquad k\\in\\mathbb Z`} />
      </div>;
    })}
    <p><strong>Połączenie i uproszczenie rodzin.</strong> Łączymy otrzymane przypadki, usuwamy powtórzenia i, gdy to możliwe, zapisujemy je krócej:</p>
    <BlockMath math={`${familiesLatex(data.families)},\\qquad k\\in\\mathbb Z`} />
  </div>;
}
export function renderExplanation({ data }) {
  const descriptions = data.validation?.kind === "reductionShift"
    ? ["Zamieniamy cosinus na sinus kąta dopełniającego.", "Pierwszy przypadek równości sinusów: argumenty różnią się o pełny okres.", "Przenosimy wyrazy z x na jedną stronę.", "Dzielimy przez dwa i upraszczamy okres.", "Drugi przypadek wymagałby tej równości; dla podanego przesunięcia jest ona niemożliwa."]
    : data.method === "halfDouble" && data.steps.length === 4 && data.steps[0].startsWith("t=\\cos")
      ? HALF_ANGLE_DESCRIPTIONS
    : data.outerAffine
    ? ["Wprowadzamy zmienną pomocniczą dla całego uwikłanego argumentu.", ...(STEP_DESCRIPTIONS[data.method] || [])]
    : (STEP_DESCRIPTIONS[data.method] || []);
  return <div style={{ textAlign: "left", lineHeight: 1.7 }}>
    <p><strong>Plan rozwiązania.</strong> {data.idea}</p>
    <div style={{ padding: "10px 14px", borderLeft: "3px solid var(--color-accent)", background: "color-mix(in srgb, var(--color-accent) 7%, transparent)", marginBottom: 16 }}><MethodPreparation data={data} /></div>
    {data.steps.map((step, index) => <div key={index}><p style={{ marginBottom: 2 }}><strong>Krok {index + 1}.</strong> {descriptions[index] || "Wykonujemy kolejne równoważne przekształcenie."}</p><BlockMath math={step} /></div>)}
    <BasicEquationResolution data={data} />
    {data.interval && <IntervalSelectionExplanation data={data} />}
    {data.equation.includes("\\operatorname{tg}") && <p><strong>Kontrola dziedziny.</strong> Otrzymane rodziny nie zerują mianownika użytego we wzorze na tangens połówkowego kąta.</p>}
  </div>;
}

export function renderUserAnswer(userAnswer = {}) {
  const method = METHODS.find((item) => item.value === userAnswer.method)?.label;
  const count = Number.isInteger(userAnswer.familyCount) ? userAnswer.familyCount : 0;
  const lines = (Array.isArray(userAnswer.families) ? userAnswer.families.slice(0, count) : []).map((value, index) => formatAffineLatexPreview(String(value?.raw ?? value ?? ""), `x_${index + 1}`, true)).filter(Boolean);
  const solutionCount = Number.isInteger(userAnswer.solutionCount) ? userAnswer.solutionCount : 0;
  const xs = (Array.isArray(userAnswer.xs) ? userAnswer.xs.slice(0, solutionCount) : []).map((value) => String(value?.raw ?? value ?? "")).filter(Boolean);
  if (!method && !lines.length && !xs.length) return <i>(brak odpowiedzi)</i>;
  return <div style={{ display: "grid", gap: 4 }}>{method && <div>{method}</div>}{lines.length > 0 && <InlineMath math={lines.join("\\qquad\\lor\\qquad{}")} />}{xs.length > 0 && <div>Rozwiązania z przedziału: {xs.join(", ")}</div>}</div>;
}

const AdvancedTrigEquationTask = { type, generateTask, generateIntervalTask, renderTask, renderInput, checkAnswer, renderAnswer, renderExplanation, renderUserAnswer };
export default AdvancedTrigEquationTask;
